import React, { useEffect } from 'react';
import { useAdminStore } from '../store/useAdminStore';

function AdminMarketing() {
  const { marketingPromos: promos, setMarketingPromos: setPromos } = useAdminStore();
  const { marketingDraft, setMarketingDraft, clearMarketingDraft, adminIdempotencyKey, regenerateAdminIdempotencyKey, deviceId } = useAdminStore();

  const fetchPromos = async () => {
    try {
      const res = await fetch('/api/admin/promo', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      setPromos(Array.isArray(data) ? data : []);
    } catch {
      console.error('Failed to fetch promos');
    }
  };

  useEffect(() => {
    fetchPromos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/promo', {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          code: marketingDraft.code.toUpperCase(),
          discount_percentage: marketingDraft.discount,
          max_usage: marketingDraft.maxUsage,
          expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
        })
      });
      if (!res.ok) {
        alert('Failed to create promo. Please check inputs and try again.');
        return;
      }
      clearMarketingDraft();
      fetchPromos();
    } catch {
      console.error('Failed to create promo');
    } finally {
      regenerateAdminIdempotencyKey();
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <span>🎯</span> Marketing & SEO Command Centre
        </h1>
        <p className="text-gray-400 text-sm mt-1">Manage promotions, UTM tracking, and search engine optimization.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* PROMO ENGINE */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>🏷️</span> Discount & Coupon Engine
          </h2>
          
          <form onSubmit={handleCreatePromo} className="mb-6 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <input 
                type="text" 
                placeholder="PROMO CODE" 
                required 
                value={marketingDraft.code}
                onChange={(e) => setMarketingDraft({ code: e.target.value })}
                aria-label="Promo Code"
                className="flex-1 bg-gray-950 border border-gray-800 text-white rounded p-2 outline-none focus:border-blue-500 uppercase"
              />
              <input 
                type="number" 
                placeholder="% Off" 
                required 
                min="1" max="100"
                value={marketingDraft.discount}
                onChange={(e) => setMarketingDraft({ discount: e.target.value })}
                aria-label="Discount Percentage"
                className="w-24 bg-gray-950 border border-gray-800 text-white rounded p-2 outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input 
                type="number" 
                placeholder="Max Uses" 
                required 
                min="1"
                value={marketingDraft.maxUsage}
                onChange={(e) => setMarketingDraft({ maxUsage: e.target.value })}
                aria-label="Maximum Usage Limit"
                className="flex-1 bg-gray-950 border border-gray-800 text-white rounded p-2 outline-none focus:border-blue-500"
              />
              <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-6 rounded transition-all">
                Create Code
              </button>
            </div>
          </form>

          <div className="overflow-y-auto max-h-64 pr-2 space-y-2">
            {promos.length === 0 ? (
              <p className="text-gray-500 text-sm italic">No active promo codes.</p>
            ) : (
              promos.slice(0, 100).map(p => (
                <div key={p.id} className="bg-gray-950 p-3 rounded border border-gray-800 flex justify-between items-center">
                  <div>
                    <span className="text-white font-bold font-mono block">{p.code}</span>
                    <span className="text-gray-500 text-xs">Uses: {p.usage_count} / {p.max_usage}</span>
                  </div>
                  <div className="text-green-400 font-bold">{p.discount_percentage}% OFF</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* UTM TRACKER */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>📊</span> UTM Campaign Tracker
          </h2>
          <p className="text-gray-400 text-sm mb-4">Traffic sources mapped from successful registrations.</p>
          
          <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 space-y-3">
             <div className="flex justify-between items-center">
               <span className="text-white font-mono text-sm">?utm_source=facebook</span>
               <span className="text-blue-400 font-bold">0 conversions</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-white font-mono text-sm">?utm_source=instagram</span>
               <span className="text-blue-400 font-bold">0 conversions</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-white font-mono text-sm">?utm_source=google</span>
               <span className="text-blue-400 font-bold">0 conversions</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-gray-500 font-mono text-sm">organic (no tag)</span>
               <span className="text-blue-400 font-bold">0 conversions</span>
             </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdminMarketing;
